import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import {
  BalanceLineKind,
  previewLockSlot,
  LockPreview,
} from '../../../services/teamBalance'

interface UseLockPreviewOptions {
  teamId: string
  /** D40 — roster-scoped, so a preview taken on roster A can never be served for roster B. */
  rosterId: string
  slotId: string | null
  /** Sep 23 — the kind of the row being invoiced; an official line's preview needs it. */
  expectedKind?: BalanceLineKind
}

/**
 * R4-#4 — fetches the EXACT server lock preview (same solver + pre-lock-credit allocator as
 * the lock itself) while the Lock modal is open. No staleness tolerance: money may move
 * between opens, so every open refetches.
 */
export const useLockPreview = ({
  teamId,
  rosterId,
  slotId,
  expectedKind,
}: UseLockPreviewOptions) => {
  const query = useQuery<LockPreview>({
    // A slot's kind never changes, so the slot id alone still names the preview.
    queryKey: queryKeys.teamBalance.lockPreview(teamId, rosterId, slotId ?? ''),
    queryFn: () =>
      previewLockSlot(teamId, rosterId, slotId as string, expectedKind),
    enabled: Boolean(teamId) && Boolean(rosterId) && slotId !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  return {
    ...query,
    preview: query.data ?? null,
  }
}

export default useLockPreview
